import * as THREE from 'three';

import { type Bone, type EulerOrder } from './core';
import {
  composeMatrixFromTransform,
  decomposeMatrixToTransform,
  eulerDegreesFromQuaternion,
  normalizeQuaternion,
  quaternionFromEulerDegrees,
  type RotationAxisTriplet,
} from './rotationService';

export type AxisTriplet = RotationAxisTriplet;

export type BonePoseTransform = {
  translate: AxisTriplet;
  rotate: AxisTriplet;
};

export type RestTranslateMode = 'local' | 'world';
export type RestRotateMode = 'local' | 'direction';

export const SCALE_IDENTITY_EPSILON = 1e-4;

function sanitizeAxisTriplet(
  values: AxisTriplet,
  fallback: AxisTriplet,
): AxisTriplet {
  return [
    Number.isFinite(values[0]) ? values[0] : fallback[0],
    Number.isFinite(values[1]) ? values[1] : fallback[1],
    Number.isFinite(values[2]) ? values[2] : fallback[2],
  ];
}

function matrixToTransform(
  matrix: THREE.Matrix4,
  order: EulerOrder,
): BonePoseTransform {
  const { position, quaternion } = decomposeMatrixToTransform(matrix);

  return {
    translate: [position.x, position.y, position.z],
    rotate: eulerDegreesFromQuaternion(quaternion, order),
  };
}

export function hasNonIdentityScale(
  scale: AxisTriplet,
  epsilon = SCALE_IDENTITY_EPSILON,
): boolean {
  return scale.some((axisValue) => Math.abs(axisValue - 1) > epsilon);
}

export function readBonePoseTransform(bone: Bone): BonePoseTransform {
  const localMatrix = new THREE.Matrix4().multiplyMatrices(
    bone.restLocal,
    bone.poseLocal,
  );
  return matrixToTransform(localMatrix, bone.eulerOrder);
}

export function readBonePoseBaselineTransform(bone: Bone): BonePoseTransform {
  return matrixToTransform(bone.restLocal, bone.eulerOrder);
}

export function writeBonePoseTransform(
  bone: Bone,
  nextTransform: BonePoseTransform,
): void {
  const currentLocal = new THREE.Matrix4().multiplyMatrices(
    bone.restLocal,
    bone.poseLocal,
  );
  const { scale: currentScale } = decomposeMatrixToTransform(currentLocal);

  const currentTransform = matrixToTransform(currentLocal, bone.eulerOrder);
  const safeTranslate = sanitizeAxisTriplet(
    nextTransform.translate,
    currentTransform.translate,
  );
  const safeRotate = sanitizeAxisTriplet(nextTransform.rotate, currentTransform.rotate);

  const nextPosition = new THREE.Vector3(
    safeTranslate[0],
    safeTranslate[1],
    safeTranslate[2],
  );
  const nextQuaternion = quaternionFromEulerDegrees(safeRotate, bone.eulerOrder);
  const nextLocal = composeMatrixFromTransform(
    nextPosition,
    nextQuaternion,
    currentScale,
  );

  const restInverse = new THREE.Matrix4().copy(bone.restLocal).invert();
  bone.poseLocal.copy(restInverse).multiply(nextLocal);
}

export function writeBonePoseLocalQuaternion(
  bone: Bone,
  localQuaternion: THREE.Quaternion,
): void {
  const { position: currentPosition, scale: currentScale } = decomposeMatrixToTransform(
    bone.poseLocal,
  );
  const nextQuaternion = normalizeQuaternion(localQuaternion.clone());
  const nextPoseLocal = composeMatrixFromTransform(
    currentPosition,
    nextQuaternion,
    currentScale,
  );
  bone.poseLocal.copy(nextPoseLocal);
}

export function readBoneRestTranslation(
  bone: Bone,
  mode: RestTranslateMode,
): AxisTriplet {
  const matrix = mode === 'world' ? bone.restWorld : bone.restLocal;
  const pos = new THREE.Vector3().setFromMatrixPosition(matrix);
  return [pos.x, pos.y, pos.z];
}

export function readBoneRestRotation(
  bone: Bone,
  mode: RestRotateMode,
): AxisTriplet {
  if (mode === 'direction') {
    return [bone.direction.x, bone.direction.y, bone.direction.z];
  }

  const transform = matrixToTransform(bone.restLocal, bone.eulerOrder);
  return transform.rotate;
}

export function readBoneRestScale(bone: Bone): AxisTriplet {
  const { scale } = decomposeMatrixToTransform(bone.restLocal);
  return [scale.x, scale.y, scale.z];
}

export function formatTransformValue(value: number): string {
  if (!Number.isFinite(value)) return '0.000';
  return value.toFixed(3);
}
